// hooks/useNetworkStatus.js
// Custom hook for monitoring network speed and status

import { useState, useEffect, useCallback } from 'react';
import { Wifi, WifiOff } from 'lucide-react';
import requestQueue from '../User/Questions/RequestQueue';

/**
 * Custom hook for monitoring network speed and status.
 * Checks network speed periodically and provides status indicators.
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether monitoring is active (default: true)
 * @param {number} options.checkInterval - Check interval in ms (default: 15000)
 * @returns {Object} - { networkStatus, networkSpeed, networkConfig, NetworkIcon }
 */
export function useNetworkStatus({
  enabled = true,
  checkInterval = 15000
} = {}) {
  const [networkStatus, setNetworkStatus] = useState("checking");
  const [networkSpeed, setNetworkSpeed] = useState(null);

  // Network speed check function
  const checkNetworkSpeed = useCallback(() => {
    const imageAddr = "https://upload.wikimedia.org/wikipedia/commons/2/2d/Snake_River_%285mb%29.jpg" + "?n=" + Math.random();
    const downloadSize = 4995374; // bytes (approx 5MB)
    const startTime = Date.now();

    // Safety timeout (5s): mark poor if not loaded
    const timeoutId = setTimeout(() => {
      setNetworkStatus("poor");
      setNetworkSpeed(null);
      try { requestQueue.setNetworkStatus("poor", 0); } catch(_) {}
    }, 5000);

    const img = new Image();
    img.onload = function () {
      clearTimeout(timeoutId);
      const durationSec = (Date.now() - startTime) / 1000;
      if (durationSec <= 0) durationSec = 0.001;
      const bitsLoaded = downloadSize * 8;
      const speedBps = bitsLoaded / durationSec;
      const speedKbps = speedBps / 1024;
      const speedMbps = speedKbps / 1024;

      // numeric value rounded to 2 decimals
      const speedNumeric = Number(speedMbps.toFixed(2));
      setNetworkSpeed(speedNumeric);

      // Thresholds: green > 5 Mbps, orange 1-5 Mbps, red < 1 Mbps
      if (speedNumeric > 5) {
        setNetworkStatus("good");
        try { requestQueue.setNetworkStatus("good", speedNumeric); } catch(_) {}
      } else if (speedNumeric >= 1) {
        setNetworkStatus("fair");
        try { requestQueue.setNetworkStatus("fair", speedNumeric); } catch(_) {}
      } else {
        setNetworkStatus("poor");
        try { requestQueue.setNetworkStatus("poor", speedNumeric); } catch(_) {}
      }
    };

    img.onerror = function () {
      clearTimeout(timeoutId);
      setNetworkStatus("poor");
      setNetworkSpeed(null);
      try { requestQueue.setNetworkStatus("poor", 0); } catch(_) {}
    };

    // Start download
    img.src = imageAddr;
  }, []);

  // Get network status config
  const getNetworkConfig = useCallback(() => {
    switch (networkStatus) {
      case 'good':
        return { variant: 'success', icon: Wifi, label: 'Good' };
      case 'fair':
        return { variant: 'warning', icon: Wifi, label: 'Moderate' };
      case 'poor':
        return { variant: 'destructive', icon: WifiOff, label: 'Poor' };
      default:
        return { variant: 'secondary', icon: Wifi, label: 'Checking...' };
    }
  }, [networkStatus]);

  // Periodic check (runs immediately and every checkInterval)
  useEffect(() => {
    if (!enabled) return;

    checkNetworkSpeed(); // initial
    const id = setInterval(() => {
      checkNetworkSpeed();
    }, checkInterval);

    return () => clearInterval(id);
  }, [enabled, checkInterval, checkNetworkSpeed]);

  const networkConfig = getNetworkConfig();
  const NetworkIcon = networkConfig.icon;

  return {
    networkStatus,
    networkSpeed,
    networkConfig,
    NetworkIcon
  };
}

export default useNetworkStatus;
